/** @typedef {import("../../api/types").OrdersPage} OrdersPage */
import React, {useEffect, useState} from "react";
import axios from "axios";
import {Link} from "react-router-dom";
import {useDebouncedSearch} from "../../hooks/useDebouncedSearch";
import {useListParams} from "../../hooks/useListParams";
import {useScrollRestore} from "../../hooks/useScrollRestore";
import OrderStatusFilter from "./components/OrderStatusFilter";
import OrdersTable from "./components/OrdersTable";
import SearchBox from "../../shared/components/SearchBox";
import {DEFAULT_STATUS_FILTER, ORDER_STATUSES} from "./utils/orderStatus";
import {Alert, Button, Container} from "reactstrap";
import IconLabel from "../../shared/components/IconLabel";
import {ChevronLeftIcon, ChevronRightIcon, PlusIcon} from "../../shared/components/icons";

const ORDERS_URL = "/os/api/service_orders/";
const DEFAULT_ORDERING = "-number"; // newest order number first
const STATUS_CODES = ORDER_STATUSES.map((s) => s.code);

const Orders = () => {
    // The list's state lives in the address, so it survives a visit to an order.
    // `statuses`: none selected means no filter, every order. `ordering` is the
    // API's value: a field, "-" in front for descending.
    const {page, search, ordering, statuses, setPage, setSearch, setOrdering, setStatuses} = useListParams({
        basePath: "/orders",
        defaultOrdering: DEFAULT_ORDERING,
        statusCodes: STATUS_CODES,
        defaultStatuses: DEFAULT_STATUS_FILTER,
    });
    // The request waits for a pause in typing; a new search starts from page 1.
    const {text: searchText, setText: setSearchText} = useDebouncedSearch(search, setSearch);
    const [data, setData] = useState(/** @type {OrdersPage|null} */ (null));
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const controller = new AbortController();
        setLoading(true);
        setError(null);

        axios
            .get(ORDERS_URL, {
                params: {page, status: statuses, search: search || undefined, ordering},
                // ?status=OP&status=CO (not status[]=OP), as the API expects
                paramsSerializer: {indexes: null},
                signal: controller.signal,
            })
            .then(({data}) => setData(data))
            .catch((err) => {
                if (axios.isCancel(err)) return;
                setError("Não foi possível carregar as ordens de serviço.");
            })
            .finally(() => {
                if (!controller.signal.aborted) setLoading(false);
            });

        return () => controller.abort();
    }, [page, statuses, search, ordering]);

    useScrollRestore(!loading);

    const emptyMessage = search
        ? `Nenhuma ordem de serviço encontrada para “${search}”.`
        : statuses.length
          ? "Nenhuma ordem de serviço com essas situações."
          : "Nenhuma ordem de serviço encontrada.";

    const orders = data?.results ?? [];
    const pagination = data?.pagination;

    return (
        <Container className="mt-4 mb-5">
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                <div className="d-flex flex-wrap align-items-center flex-grow-1 gap-2">
                    <OrderStatusFilter selected={statuses} onChange={setStatuses} />
                    <SearchBox
                        value={searchText}
                        onChange={setSearchText}
                        placeholder="Buscar por cliente ou observações"
                        ariaLabel="Buscar ordens de serviço"
                    />
                </div>
                <Button color="primary" tag={Link} to="/orders/new">
                    <IconLabel icon={<PlusIcon />}>Criar</IconLabel>
                </Button>
            </div>
            {error && <Alert color="danger">{error}</Alert>}
            <OrdersTable
                orders={loading ? null : orders}
                sort={{ordering, onChange: setOrdering}}
                emptyMessage={error ? undefined : emptyMessage}
            />
            {pagination && pagination.total_pages > 1 && (
                <div className="d-flex justify-content-between align-items-center">
                    <Button
                        color="primary"
                        outline
                        disabled={loading || !pagination.prev_page}
                        onClick={() => setPage(pagination.prev_page)}
                    >
                        <IconLabel icon={<ChevronLeftIcon />}>Anterior</IconLabel>
                    </Button>
                    <span>
                        Página {pagination.curr_page} de {pagination.total_pages}
                    </span>
                    <Button
                        color="primary"
                        outline
                        disabled={loading || !pagination.next_page}
                        onClick={() => setPage(pagination.next_page)}
                    >
                        <IconLabel icon={<ChevronRightIcon />}>Próxima</IconLabel>
                    </Button>
                </div>
            )}
        </Container>
    );
};

export default Orders;
